<template>
  <!-- State: No Tasks | No Results | Error -->
  <div class="h-full flex justify-center" v-show="state">
    <slot name="state" />
  </div>

  <!-- Data: Kanban -->
  <div
    v-show="!state"
    :key="kanbanRefreshKey"
    class="kanban-board w-full h-full absolute grid grid-cols-[repeat(3,_minmax(360px,_1fr))] py-4 px-4 gap-1 transition"
  >
    <TasksKanbanColumn
      v-for="(column, index) in columns"
      :key="index"
      :status="column.status"
      :allTasks="allTasks"
      @click:task="emit('show:detail', $event)"
      @update:loading="handleUpdateLoading"
      @update:allTasks="handleUpdateAllTasks"
      @update:dragging="handleUpdateDragging"
      @refresh:kanban="handleRefreshKanban"
      :disableDragging="disableDragging"
      :class="{
        'bg-utility-gray-200 hover:bg-utility-blue-200 rounded-xl': dragging,
      }"
    />
    <ScheduleTask
      :open="!!scheduledTask"
      :task="scheduledTask"
      :key="scheduledTaskKey"
    />
  </div>
</template>

<script setup>
// Import Vue Utilities
import { ref, inject, computed } from "vue";

// Import Utilities
import { uniqBy } from "lodash";
import { storeToRefs } from "pinia";

// Import Components
import TasksKanbanColumn from "./TasksKanbanColumn.vue";
import { ScheduleTask } from "@/Pages/Tasks/Modals";

// Import Composables
import { useFilters } from "@/Composables/useFilters";
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:fetchingStatus",
]);

// Emitter
const emitter = inject("emitter");

// Props
const props = defineProps({
  count: { type: Number, default: null },
  fetchingStatus: { type: String, required: true },
});

// Refs
const scheduledTaskKey = ref(0);
const scheduledTask = ref(undefined);
const allTasks = ref([]);
const dragging = ref(false);
const kanbanRefreshKey = ref(0);

const { DefaultView } = useDefaultTasksViews();
const filterStore = useFilters("tasks");
const { activeView } = storeToRefs(filterStore);

const disableDragging = computed(() => {
  return activeView.value.id === DefaultView.ARCHIVED.id;
});

const columns = ref([
  {
    status: "open",
    fetchingStatus: "loading",
    count: null,
  },
  {
    status: "scheduled",
    fetchingStatus: "loading",
    count: null,
  },
  {
    status: "completed",
    fetchingStatus: "loading",
    count: null,
  },
]);

// Computed Props
const state = computed(() => {
  if (props.fetchingStatus === "loading") {
    return false;
  }

  return props.count === 0 || props.fetchingStatus === "error";
});

// Functions
const getColumn = (status) => {
  return columns.value.find((column) => column.status === status);
};

const handleUpdateDragging = (value) => {
  dragging.value = value;
};

const handleUpdateLoading = ({ status, fetchingStatus, count }) => {
  const column = getColumn(status);

  if (column) {
    column.count = count;
    column.fetchingStatus = fetchingStatus;
  }

  handleUpdateCount(
    columns.value.reduce((acc, column) => acc + column.count, 0)
  );

  handleUpdateFetchingStatus(
    columns.value.map((column) => column.fetchingStatus)
  );
};

const handleUpdateCount = (count) => {
  emit("update:count", count);
};

const handleUpdateFetchingStatus = (fetchingStatuses) => {
  let state = "loading";

  if (fetchingStatuses.every((fetchingStatus) => fetchingStatus === "loaded")) {
    state = "loaded";
  }

  if (fetchingStatuses.some((fetchingStatus) => fetchingStatus === "error")) {
    state = "error";
  }

  emit("update:fetchingStatus", state);
};

const handleUpdateAllTasks = (tasks) => {
  allTasks.value = uniqBy([...allTasks.value, ...tasks], "id");
};

const handleRefreshKanban = () => {
  kanbanRefreshKey.value++;
};

// Emitters
emitter.on("scheduling-task", (task) => {
  scheduledTask.value = task;
  scheduledTaskKey.value++;
});

emitter.on("undo-drag-action", () => {
  scheduledTask.value = undefined;
});

emitter.on("update-column", () => {
  scheduledTask.value = undefined;
});
</script>
